import React from 'react';
import { useTranslation } from 'react-i18next';
import { CategoryAttribute, SellerProductBasicDetails } from './types';
import InfoText from '../../atoms/InfoText';
import TitleText from '../../atoms/TitleText';
import ProductCertificationsSection from './ProductCertificationsSection';
import ProductOemSection from './ProductOemSection';
import ProductVariantsTable from './ProductVariantsTable';

type Props = {
  basicDetails?: SellerProductBasicDetails;
  categoryAttributes?: CategoryAttribute[];
};

export default function ProductTechnicalSpecsSection({
  basicDetails,
  categoryAttributes,
}: Props) {
  const { t } = useTranslation();

  const logistics = [
    {
      name: t('product.productDimensions'),
      value: basicDetails?.cbm,
    },
    {
      name: t('product.netWeight'),
      value: basicDetails?.grossWeight
        ? `${basicDetails.grossWeight} kg`
        : null,
    },
    {
      name: t('product.cartonDetails'),
      value: basicDetails?.itemPerCarton
        ? `${basicDetails.itemPerCarton} ${t('components.productDetailsPage.units')}`
        : null,
    },
    {
      name: t('product.incoterms'),
      value: basicDetails?.incoterm,
    },

    
  ].filter((item) => item.value !== undefined && item.value !== null);

  const displaySpecs = [...(basicDetails?.specs || [])];

  return (
    <div
      className="flex flex-col w-full gap-4"
      data-testid="seller-product-specs-section"
    >
      <InfoText
        text={t('components.productDetailsPage.technicalSpecifications')}
        className="!text-gray-400 !text-sm !m-0"
      />

      <div
        className="flex flex-col gap-6"
        data-testid="seller-product-specs-grid"
      >
        {/* Material & Construction */}
        <div data-testid="seller-product-specs-column">
          <TitleText
            text={t('components.productDetailsPage.materialConstruction')}
            size="base"
            className="!font-bold mb-3 text-gray-900 dark:text-white"
          />
          <div
            className="flex flex-col"
            data-testid="seller-product-specs-list"
          >
            {!displaySpecs.length ? (
              <InfoText text="—" className="!text-gray-400 p-3" />
            ) : (
              displaySpecs.map((spec, idx) => (
                <div
                  key={`spec-${idx}-${spec.name}`}
                  className="flex justify-between items-center p-3 text-sm even:bg-gray-50 dark:even:bg-gray-800/50 rounded-lg"
                  data-testid={`seller-product-spec-row-${idx}`}
                >
                  <InfoText
                    text={spec.name}
                    className="!text-light-text-primary !text-sm dark:!text-gray-300 !font-normal !m-0"
                  />
                  <InfoText
                    text={spec.value || '—'}
                    className="!font-semibold !text-[#090909] dark:!text-white !text-sm !text-right !m-0"
                  />
                </div>
              ))
            )}
          </div>
        </div>

        {/* Dimensions & Logistics */}
        <div data-testid="seller-product-logistics-column">
          <TitleText
            text={t('components.productDetailsPage.dimensionsLogistics')}
            className="!font-bold mb-3 !text-[#090909] dark:!text-white !text-base"
          />
          <div
            className="flex flex-col"
            data-testid="seller-product-logistics-list"
          >
            {logistics.map((item, idx) => (
              <div
                key={`logistics-${idx}-${item.name}`}
                className="flex justify-between items-center p-3 text-sm even:bg-gray-50 dark:even:bg-gray-800/50 rounded-lg"
                data-testid={`seller-product-logistics-row-${idx}`}
              >
                <InfoText
                  text={item.name}
                  className="!text-light-text-primary !text-sm dark:!text-gray-300 !font-normal !m-0"
                />
                <InfoText
                  text={
                    item.value !== undefined && item.value !== null
                      ? String(item.value)
                      : '—'
                  }
                  className="!font-semibold !text-[#090909] dark:!text-white !text-sm !text-right !m-0"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <ProductVariantsTable
        basicDetails={basicDetails}
        categoryAttributes={categoryAttributes}
      />
      <ProductCertificationsSection basicDetails={basicDetails} />
      <ProductOemSection basicDetails={basicDetails} />
    </div>
  );
}
